import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { API_BASE } from "@/lib/api";

const NotificationsContext = createContext(null);

const MAX_RECENT = 20;

const NOTIF_ICON = "/assets/orbit-logo.svg";

// Roles allowed to receive ticket notifications
const NOTIFIABLE_ROLES = ["agent", "supervisor"];

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const eventSourceRef = useRef(null);
  const audioRef = useRef(null);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const [recent, setRecent] = useState([]); // {id, type, title, body, at, link, read}
  const [unread, setUnread] = useState(0);

  const pushItem = useCallback((item) => {
    setRecent((prev) => [item, ...prev].slice(0, MAX_RECENT));
    if (!item.read) setUnread((n) => n + 1);
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "denied";
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      return p;
    } catch {
      return "denied";
    }
  }, []);

  const showOsNotification = useCallback((title, body, link) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: NOTIF_ICON,
        badge: NOTIF_ICON,
        tag: link || title, // collapse duplicates
        renotify: true,
      });
      n.onclick = () => {
        window.focus();
        if (link) nav(link);
        n.close();
      };
    } catch {
      // older browsers
    }
  }, [nav]);

  const markAllRead = useCallback(() => {
    setRecent((prev) => prev.map((r) => ({ ...r, read: true })));
    setUnread(0);
  }, []);

  // SSE connection lifecycle — open when logged in, close on logout
  useEffect(() => {
    if (!user) {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      return;
    }
    // Only roles that handle tickets need notifications
    if (!NOTIFIABLE_ROLES.includes(user.role)) {
      return;
    }
    const token = localStorage.getItem("tara_token");
    if (!token) return;

    const url = `${API_BASE}/events/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    const handleNewTicket = (e) => {
      try {
        const d = JSON.parse(e.data);
        const sysLabel = d.system === "SATURNUS" ? "STPAY" : d.system === "TARAPAY" ? "TARA" : (d.system || "");
        const merchant = d.merchant_name || d.customer_name || "—";
        const orderRef = d.merchant_order_number || d.rrn_number || "";
        const title = `[${sysLabel}] Tiket Baru ${d.ticket_number || ""}`;
        const body = `${merchant}${orderRef ? ` · ${orderRef}` : ""} · ${d.service_type || ""}`;
        const link = d.id ? `/tickets/${d.id}` : "/tickets";
        showOsNotification(title, body, link);
        pushItem({
          id: `nt-${d.id}-${d.at}`,
          type: "new_ticket",
          title,
          body,
          link,
          at: d.at,
          read: false,
        });
      } catch (_) { /* malformed payload */ }
    };

    const handleInboundComment = (e) => {
      try {
        const d = JSON.parse(e.data);
        const sysLabel = d.system === "SATURNUS" ? "STPAY" : d.system === "TARAPAY" ? "TARA" : "";
        const who = d.customer_name || d.merchant_name || "Customer";
        const title = `[${sysLabel}] Balasan baru di ${d.ticket_number || "tiket"}`;
        const body = `${who}: ${d.preview || ""}`.slice(0, 200);
        const link = d.ticket_id ? `/tickets/${d.ticket_id}` : "/tickets";
        showOsNotification(title, body, link);
        pushItem({
          id: `ic-${d.ticket_id}-${d.at}`,
          type: "inbound_comment",
          title,
          body,
          link,
          at: d.at,
          read: false,
        });
      } catch (_) { /* malformed payload */ }
    };

    es.addEventListener("new_ticket", handleNewTicket);
    es.addEventListener("inbound_comment", handleInboundComment);
    es.addEventListener("ready", () => { /* connected */ });
    es.onerror = () => {
      // Auto-reconnect handled by browser by default; do nothing
    };

    return () => {
      es.removeEventListener("new_ticket", handleNewTicket);
      es.removeEventListener("inbound_comment", handleInboundComment);
      es.close();
      eventSourceRef.current = null;
    };
  }, [user, showOsNotification, pushItem]);

  // Auto-prompt for Notification permission on first login (for notifiable roles)
  useEffect(() => {
    if (!user) return;
    if (!NOTIFIABLE_ROLES.includes(user.role)) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      // Small delay so it doesn't fire right at login
      const t = setTimeout(() => { requestPermission(); }, 1500);
      return () => clearTimeout(t);
    }
  }, [user, requestPermission]);

  const value = {
    permission,
    requestPermission,
    recent,
    unread,
    markAllRead,
    notifiable: !!user && NOTIFIABLE_ROLES.includes(user.role),
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <audio ref={audioRef} src="" preload="none" />
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) return { permission: "default", requestPermission: () => {}, recent: [], unread: 0, markAllRead: () => {}, notifiable: false };
  return ctx;
};
